<template>
  <div class="w-full space-y-6">
    <!-- Connection Details -->
    <Card class="w-full">
      <CardHeader>
        <CardTitle>Connection Details</CardTitle>
        <CardDescription>
          Use these connection details to connect to your kafkadirect destination
        </CardDescription>
      </CardHeader>
      <CardContent class="space-y-4">
        <!-- Loading State -->
        <div v-if="!connectGuide" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-for="i in 6" :key="i" class="space-y-2">
              <Skeleton class="h-4 w-24" />
              <Skeleton class="h-12 w-full" />
            </div>
          </div>
          <div class="space-y-2">
            <Skeleton class="h-4 w-32" />
            <Skeleton class="h-12 w-full" />
          </div>
          <div class="space-y-2">
            <Skeleton class="h-4 w-16" />
            <div class="flex gap-2">
              <Skeleton class="h-6 w-20" />
              <Skeleton class="h-6 w-16" />
              <Skeleton class="h-6 w-24" />
            </div>
          </div>
        </div>

        <!-- Content State -->
        <div v-else>
          <!-- Kafka Brokers - Full Width -->
          <div class="space-y-2">
            <Label id="kafka-brokers-label" class="text-sm font-medium text-foreground">Kafka Brokers</Label>
            <div class="relative group">
              <div class="p-3 bg-muted/50 rounded-md border text-sm font-mono break-all pr-12" aria-labelledby="kafka-brokers-label" role="textbox" aria-readonly="true">
                {{ connectGuide.kafka_proxy_endpoint || 'N/A' }}
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      v-if="connectGuide.kafka_proxy_endpoint"
                      variant="ghost"
                      size="sm"
                      class="absolute right-1 top-1 h-8 w-8 p-0 transition-opacity"
                      :aria-label="`Copy Kafka Brokers: ${connectGuide.kafka_proxy_endpoint}`"
                      @click="copyToClipboard(connectGuide.kafka_proxy_endpoint, 'brokers')"
                      :disabled="copyStates.brokers"
                    >
                      <Check v-if="copyStates.brokers" class="h-3 w-3 text-green-600" />
                      <Clipboard v-else class="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{{ copyStates.brokers ? 'Copied!' : 'Copy to clipboard' }}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          <!-- Other Connection Details - 2 Column Layout -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <!-- Security Protocol -->
            <div class="space-y-2">
              <Label id="security-protocol-label" class="text-sm font-medium text-foreground">Security Protocol</Label>
              <div class="p-3 bg-muted/50 rounded-md border text-sm" aria-labelledby="security-protocol-label" role="textbox" aria-readonly="true">
                {{ connectGuide.security_protocol || 'N/A' }}
              </div>
            </div>

            <!-- SASL Mechanism -->
            <div class="space-y-2">
              <Label id="sasl-mechanism-label" class="text-sm font-medium text-foreground">SASL Mechanism</Label>
              <div class="p-3 bg-muted/50 rounded-md border text-sm" aria-labelledby="sasl-mechanism-label" role="textbox" aria-readonly="true">
                {{ connectGuide.sasl_mechanism || 'N/A' }}
              </div>
            </div>

            <!-- Username -->
            <div class="space-y-2">
              <Label id="username-label" class="text-sm font-medium text-foreground">Username</Label>
              <div class="relative group">
                <div class="p-3 bg-muted/50 rounded-md border text-sm font-mono pr-12" aria-labelledby="username-label" role="textbox" aria-readonly="true">
                  {{ connectGuide.username || 'N/A' }}
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        v-if="connectGuide.username"
                        variant="ghost"
                        size="sm"
                        class="absolute right-1 top-1 h-8 w-8 p-0 transition-opacity"
                        :aria-label="`Copy Username: ${connectGuide.username}`"
                        @click="copyToClipboard(connectGuide.username, 'username')"
                        :disabled="copyStates.username"
                      >
                        <Check v-if="copyStates.username" class="h-3 w-3 text-green-600" />
                        <Clipboard v-else class="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{{ copyStates.username ? 'Copied!' : 'Copy to clipboard' }}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <!-- Password -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <Label id="password-label" class="text-sm font-medium text-foreground">Password</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info class="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>This is the password you set when creating the kafkadirect destination</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div class="p-3 bg-muted/50 rounded-md border text-sm italic text-muted-foreground" aria-labelledby="password-label" role="textbox" aria-readonly="true">
                The password you entered when creating the kafkadirect destination
              </div>
            </div>

            <!-- Consumer Group Prefix -->
            <div class="space-y-2">
              <Label id="consumer-group-label" class="text-sm font-medium text-foreground">Consumer Group Prefix</Label>
              <div class="relative group">
                <div class="p-3 bg-muted/50 rounded-md border text-sm font-mono pr-12" aria-labelledby="consumer-group-label" role="textbox" aria-readonly="true">
                  {{ connectGuide.consumer_group_prefix || 'N/A' }}
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        v-if="connectGuide.consumer_group_prefix"
                        variant="ghost"
                        size="sm"
                        class="absolute right-1 top-1 h-8 w-8 p-0 transition-opacity"
                        :aria-label="`Copy Consumer Group Prefix: ${connectGuide.consumer_group_prefix}`"
                        @click="copyToClipboard(connectGuide.consumer_group_prefix, 'consumerGroup')"
                        :disabled="copyStates.consumerGroup"
                      >
                        <Check v-if="copyStates.consumerGroup" class="h-3 w-3 text-green-600" />
                        <Clipboard v-else class="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{{ copyStates.consumerGroup ? 'Copied!' : 'Copy to clipboard' }}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <!-- Schema Registry URL -->
            <div class="space-y-2">
              <Label id="schema-registry-label" class="text-sm font-medium text-foreground">Schema Registry URL</Label>
              <div class="relative group">
                <div class="p-3 bg-muted/50 rounded-md border text-sm font-mono break-all pr-12" aria-labelledby="schema-registry-label" role="textbox" aria-readonly="true">
                  {{ connectGuide.schema_registry_url || 'N/A' }}
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        v-if="connectGuide.schema_registry_url"
                        variant="ghost"
                        size="sm"
                        class="absolute right-1 top-1 h-8 w-8 p-0 transition-opacity"
                        :aria-label="`Copy Schema Registry URL: ${connectGuide.schema_registry_url}`"
                        @click="copyToClipboard(connectGuide.schema_registry_url, 'schemaRegistry')"
                        :disabled="copyStates.schemaRegistry"
                      >
                        <Check v-if="copyStates.schemaRegistry" class="h-3 w-3 text-green-600" />
                        <Clipboard v-else class="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{{ copyStates.schemaRegistry ? 'Copied!' : 'Copy to clipboard' }}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>

          <!-- Topics -->
          <div class="space-y-2 mt-4">
            <Label id="topics-label" class="text-sm font-medium text-foreground">Topics</Label>
            <div class="flex flex-wrap gap-2" aria-labelledby="topics-label" role="list">
              <Badge 
                v-for="topic in connectGuide.topics || []" 
                :key="topic" 
                variant="secondary"
                class="text-xs"
              >
                {{ topic }}
              </Badge>
              <div v-if="!connectGuide.topics?.length" class="flex items-center gap-2 text-sm text-muted-foreground italic">
                <div class="h-4 w-4 rounded-full bg-muted flex items-center justify-center">
                  <div class="h-2 w-2 rounded-full bg-muted-foreground/50" />
                </div>
                No topics available
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <!-- Visual Separator -->
    <Separator class="my-6" />

    <!-- Python Code Example -->
    <Card class="w-full">
      <CardHeader>
        <CardTitle class="flex items-center justify-between">
          <span>Python Code Example</span>
          <Button
            v-if="connectGuide && !isCopyingPython"
            variant="outline"
            size="sm"
            class="h-8 px-3"
            title="Copy to clipboard"
            @click="copyPythonCode"
            :disabled="!connectGuide.python_code"
          >
            <Clipboard class="h-4 w-4 mr-2" />
            Copy
          </Button>
          <Button 
            v-else-if="connectGuide && isCopyingPython"
            variant="outline" 
            size="sm" 
            class="h-8 px-3" 
            disabled
          >
            <Check class="h-4 w-4 mr-2 text-green-600" />
            Copied!
          </Button>
        </CardTitle>
        <CardDescription>
          Use this Python code to connect and consume from your kafkadirect destination
        </CardDescription>
      </CardHeader>
      <CardContent>
        <!-- Loading State -->
        <div v-if="!connectGuide" class="space-y-2">
          <Skeleton class="h-4 w-full" />
          <Skeleton class="h-4 w-3/4" />
          <Skeleton class="h-4 w-5/6" />
          <Skeleton class="h-4 w-2/3" />
          <Skeleton class="h-4 w-4/5" />
          <Skeleton class="h-4 w-1/2" />
        </div>
        
        <!-- Content State -->
        <div v-else-if="connectGuide.python_code" class="relative">
          <ScrollArea class="h-96 w-full rounded-md border">
            <pre class="bg-card p-4 text-sm"><code v-html="highlightedPythonCode"></code></pre>
          </ScrollArea>
        </div>
        
        <!-- Empty State -->
        <div v-else class="p-8 text-center">
          <div class="flex flex-col items-center gap-3 text-muted-foreground">
            <div class="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <Clipboard class="h-6 w-6" />
            </div>
            <p class="text-sm italic">No Python code available</p>
            <p class="text-xs text-muted-foreground/80">Code will appear here once the connection guide is configured</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from 'vue';
import { Clipboard, Check, Info } from 'lucide-vue-next';
import { sanitizeHtml } from '@/utils/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Prism from 'prismjs';
import 'prismjs/components/prism-python.min';
import 'prismjs/themes/prism.css';

const props = defineProps({
  connectGuide: {
    type: Object,
    default: () => null
  }
});

const isCopyingPython = ref(false);
const copyStates = reactive({
  brokers: false,
  username: false,
  consumerGroup: false,
  schemaRegistry: false
});

const highlightedPythonCode = computed(() => {
  if (!props.connectGuide?.python_code) return '';
  
  const highlighted = Prism.highlight(
    props.connectGuide.python_code,
    Prism.languages.python,
    'python'
  );
  
  return sanitizeHtml(highlighted);
});

const copyToClipboard = async (text, field) => {
  if (!text || copyStates[field]) return;
  
  copyStates[field] = true;
  
  try {
    await navigator.clipboard.writeText(text);
    
    setTimeout(() => {
      copyStates[field] = false;
    }, 1000);
  } catch (err) {
    console.error(`Could not copy ${field}:`, err);
    copyStates[field] = false;
  }
};

const copyPythonCode = async () => {
  if (!props.connectGuide?.python_code) return;
  
  isCopyingPython.value = true;
  
  try {
    await navigator.clipboard.writeText(props.connectGuide.python_code);
    
    setTimeout(() => {
      isCopyingPython.value = false;
    }, 1000);
  } catch (err) {
    console.error('Could not copy Python code: ', err);
    isCopyingPython.value = false;
  }
};
</script>

<style scoped>
/* Custom styling for syntax highlighting */
:deep(.token.operator) {
  color: hsl(var(--muted-foreground));
}

:deep(.token.keyword) {
  font-weight: bold;
  color: #07a;
}

:deep(.token.string) {
  color: #690;
}

:deep(.token.comment) {
  color: hsl(var(--muted-foreground));
  font-style: italic;
}

:deep(.token.function) {
  color: #dd4a68;
}

:deep(.token.number) {
  color: #905;
}

/* Ensure proper text wrapping for long content */
.break-all {
  word-break: break-all;
}
</style>